'use client';

import { useState, useEffect } from 'react';
import {
  Users,
  UserPlus,
  Mail,
  Shield,
  User,
  Search,
  MoreVertical,
  Edit2,
  Trash2,
  Loader2,
  AlertCircle,
  CheckCircle,
  X,
  Key,
  Camera,
  Upload,
  Lock,
  Info,
  Smartphone,
} from 'lucide-react';
import Avatar from '@/components/Avatar';
import { useTranslation } from '@/providers/I18nProvider';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useUsers } from '@/hooks/useUsers';
import { useAgent } from '@/hooks/useAgent';
import { useToast } from '@/hooks/useToast';

interface UserManagementProps {
  agentId: string;
  currentUserId?: string;
  authMethod?: 'PASSWORD' | 'SAML' | 'BOTH';
}

export default function UserManagement({ agentId, currentUserId, authMethod: initialAuthMethod = 'PASSWORD' }: UserManagementProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<any>(null);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  // Use feature flags hook
  const { chatMaxUsers } = useFeatureFlags(agentId);

  // Use agent hook
  const { authMethod, organizationId, configDoc, agentType, isLoading: agentLoading } = useAgent(agentId);
  const requires2FA = configDoc?.twoFactorConfig?.required;
  const isClientApi = agentType === 'WIDGET';

  // Use users hook
  const { 
    users, 
    isLoading: loading, 
    isError, 
    error: fetchError,
    inviteUser,
    updateUser,
    deleteUser,
    resetUserPassword,
    uploadAvatar
  } = useUsers(agentId);

  // Handle fetch error
  useEffect(() => {
    if (isError && fetchError) {
      const errorMessage = fetchError instanceof Error ? fetchError.message : 'Failed to load users';
      setError(errorMessage);
      
      // If not authenticated, show link to admin sign-in
      if (errorMessage.includes('401') && !errorMessage.includes('admin role')) {
        window.location.href = `/agents/${agentId}/chat/auth/admin`;
      }
    }
  }, [isError, fetchError, agentId]);

  // Invite form state
  const [inviteForm, setInviteForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'user' as 'admin' | 'user',
    authType: 'PASSWORD' as 'PASSWORD' | 'SAML',
  });

  // Update role to admin when WIDGET agent type is loaded
  useEffect(() => {
    if (isClientApi && inviteForm.role !== 'admin') {
      setInviteForm(prev => ({ ...prev, role: 'admin' }));
    }
  }, [isClientApi, inviteForm.role]);

  // Invite user
  const handleInviteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // For WIDGET agents, always force role to admin
      const submissionData = {
        ...inviteForm,
        role: isClientApi ? 'admin' : inviteForm.role,
      };
      
      await inviteUser.mutateAsync(submissionData);
      showToast(t('admin.users.invite.success'), 'success');
      setShowInviteModal(false);
      setInviteForm({ 
        email: '', 
        firstName: '', 
        lastName: '', 
        role: isClientApi ? 'admin' : 'user', 
        authType: 'PASSWORD' 
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.users.errors.inviteFailed'));
    }
  };

  // Update user
  const handleUpdateUser = async (userId: string, updates: any) => {
    try {
      await updateUser.mutateAsync({ userId, data: updates });
      showToast(t('admin.users.edit.success'), 'success');
      setShowEditModal(false);
      setEditingUser(null);
      setAvatarPreview(null);
    } catch (err) {
      setError(t('admin.users.errors.updateFailed'));
    }
  };

  // Handle avatar upload
  const handleAvatarUpload = async (file: File, userId: string) => {
    if (!file) return;

    setUploadingAvatar(true);
    try {
      const { avatarUrl } = await uploadAvatar.mutateAsync({ userId, file });
      
      // Update the editing user state
      if (editingUser && editingUser.id === userId) {
        setEditingUser({ ...editingUser, avatarUrl });
      }
      
      showToast(t('admin.users.edit.avatarSuccess'), 'success');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.users.errors.avatarUploadFailed'));
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Handle avatar file selection
  const handleAvatarFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Preview the image
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload the avatar
    if (editingUser) {
      handleAvatarUpload(file, editingUser.id);
    }
  };

  // Delete user
  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      await deleteUser.mutateAsync(userToDelete.id);
      showToast(t('admin.users.delete.success'), 'success');
      setShowDeleteModal(false);
      setUserToDelete(null);
    } catch (err) {
      setError(t('admin.users.errors.deleteFailed'));
    }
  };

  // Open delete confirmation
  const openDeleteConfirmation = (user: any) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const query = searchQuery.toLowerCase();
    return (
      user.email.toLowerCase().includes(query) ||
      user.firstName.toLowerCase().includes(query) ||
      user.lastName.toLowerCase().includes(query)
    );
  });

  // Check if user limit is exceeded
  const isUserLimitExceeded = users.length >= chatMaxUsers;
  const vectorizeBaseUrl = process.env.NEXT_PUBLIC_VECTORIZE_BASE_URL || 'https://app.vectorize.io';

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-6xl mx-auto p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{t('admin.users.title')}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('admin.users.description')}
          </p>
        </div>

        {/* User Limit Status */}
        {isUserLimitExceeded && (
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  {t('admin.users.limitExceeded.title', { limit: chatMaxUsers })}
                </p>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  {t('admin.users.limitExceeded.description')}
                </p>
                {organizationId && (
                  <a
                    href={`${vectorizeBaseUrl}/organization/${organizationId}/admin/billing/changeplan`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-2 text-sm font-medium text-blue-700 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                  >
                    {t('admin.users.limitExceeded.upgradeLink')}
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Actions Bar */}
        <div className="mb-6 flex items-center justify-between gap-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('admin.users.searchPlaceholder')}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                         bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                         focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
              />
            </div>
          </div>
          <button
            onClick={() => !isUserLimitExceeded && setShowInviteModal(true)}
            disabled={isUserLimitExceeded}
            className={`px-4 py-2 font-medium rounded-lg transition-colors flex items-center gap-2 ${
              isUserLimitExceeded
                ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
            title={isUserLimitExceeded ? t('admin.users.limitExceeded.buttonTooltip') : ''}
          >
            {isUserLimitExceeded ? (
              <Lock className="h-4 w-4" />
            ) : (
              <UserPlus className="h-4 w-4" />
            )}
            {t('admin.users.inviteButton')}
          </button>
        </div>

        {/* Success/Error Messages */}
        {error && (
          <div className="mb-6 flex items-start gap-2 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}


        {/* Users Table */}
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
          {loading ? (
            <div>
              {/* Table header skeleton */}
              <div className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-3">
                <div className="grid grid-cols-7 gap-4">
                  <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-4 w-12 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                </div>
              </div>
              {/* Table rows skeleton */}
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {[1, 2, 3, 4, 5].map((index) => (
                  <div key={index} className="px-6 py-4 animate-pulse">
                    <div className="grid grid-cols-7 gap-4 items-center">
                      {/* User column */}
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                        <div className="space-y-2">
                          <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
                          <div className="h-3 w-40 bg-gray-200 dark:bg-gray-700 rounded"></div>
                        </div>
                      </div>
                      {/* Role column */}
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                        <div className="h-4 w-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      </div>
                      {/* Auth type column */}
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                        <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      </div>
                      {/* Status column */}
                      <div className="h-6 w-16 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                      {/* Last login column */}
                      <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      {/* Created column */}
                      <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      {/* Actions column */}
                      <div className="flex items-center justify-end gap-2">
                        <div className="h-4 w-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                        <div className="h-4 w-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {searchQuery ? t('admin.users.empty.searchNoResults') : t('admin.users.empty.noUsers')}
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('admin.users.table.user')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('admin.users.table.role')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('admin.users.table.authType')}
                  </th>
                  {requires2FA && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('admin.users.table.twoFAMethod')}
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('admin.users.table.status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('admin.users.table.lastLogin')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('admin.users.table.created')}
                  </th>
                  <th className="relative px-6 py-3">
                    <span className="sr-only">{t('admin.users.table.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <Avatar
                          src={user.avatarUrl}
                          firstName={user.firstName}
                          lastName={user.lastName}
                          size="md"
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {user.role === 'admin' ? (
                          <>
                            <Shield className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                            <span className="text-sm font-medium text-blue-600 dark:text-blue-400">{t('admin.users.roles.admin')}</span>
                          </>
                        ) : (
                          <>
                            <User className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                            <span className="text-sm text-gray-900 dark:text-gray-100">{t('admin.users.roles.user')}</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {user.authType === 'SAML' ? (
                          <>
                            <Key className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                            <span className="text-sm text-purple-600 dark:text-purple-400">{t('admin.users.authTypes.saml')}</span>
                          </>
                        ) : (
                          <>
                            <Key className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                            <span className="text-sm text-gray-900 dark:text-gray-100">{t('admin.users.authTypes.password')}</span>
                          </>
                        )}
                      </div>
                    </td>
                    {requires2FA && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.authType === 'SAML' ? (
                          <span className="text-gray-400">—</span>
                        ) : user.totpEnabled ? (
                          <div className="flex items-center gap-2">
                            <Smartphone className="h-4 w-4 text-green-600 dark:text-green-400" />
                            <span className="text-sm text-green-600 dark:text-green-400">{t('admin.users.twoFA.totp')}</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                            <span className="text-sm text-blue-600 dark:text-blue-400">{t('admin.users.twoFA.email')}</span>
                          </div>
                        )}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          user.isActive
                            ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300'
                        }`}
                      >
                        {user.isActive ? t('admin.users.status.active') : t('admin.users.status.inactive')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : t('admin.users.lastLogin.never')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => {
                            setEditingUser(user);
                            setAvatarPreview(null);
                            setShowEditModal(true);
                          }}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          title={t('admin.users.actions.edit')}
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        {user.id !== currentUserId && (
                          <button
                            onClick={() => openDeleteConfirmation(user)}
                            className="text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                            title={t('admin.users.actions.delete')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowInviteModal(false)} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="relative bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">{t('admin.users.invite.title')}</h2>
                <form onSubmit={handleInviteUser} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('admin.users.invite.emailLabel')}
                    </label>
                    <input
                      type="email"
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                               bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                               focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                    />
                  </div>
                  {/* Only show name fields for password auth or when password is selected in BOTH mode */}
                  {(authMethod === 'PASSWORD' || (authMethod === 'BOTH' && inviteForm.authType === 'PASSWORD')) && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('admin.users.invite.firstNameLabel')}
                        </label>
                        <input
                          type="text"
                          value={inviteForm.firstName}
                          onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })}
                          required
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                                   bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                                   focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('admin.users.invite.lastNameLabel')}
                        </label>
                        <input
                          type="text"
                          value={inviteForm.lastName}
                          onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })}
                          required
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                                   bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                                   focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                        />
                      </div>
                    </div>
                  )}
                  {/* Show info message for SAML users */}
                  {(authMethod === 'SAML' || (authMethod === 'BOTH' && inviteForm.authType === 'SAML')) && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        {t('admin.users.invite.samlAutoPopulateDescription')}
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('profile.fields.role')}
                    </label>
                    {agentLoading ? (
                      <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                                    bg-gray-50 dark:bg-gray-800/50 text-gray-900 dark:text-gray-100">
                        Loading...
                      </div>
                    ) : isClientApi ? (
                      <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                                    bg-gray-50 dark:bg-gray-800/50 text-gray-900 dark:text-gray-100">
                        {t('admin.users.roles.admin')}
                      </div>
                    ) : (
                      <select
                        value={inviteForm.role}
                        onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value as 'admin' | 'user' })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                                 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                                 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                      >
                        <option value="user">{t('admin.users.roles.user')}</option>
                        <option value="admin">{t('admin.users.roles.admin')}</option>
                      </select>
                    )}
                  </div>
                  {authMethod === 'BOTH' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('admin.users.invite.authTypeLabel')}
                      </label>
                      <select
                        value={inviteForm.authType}
                        onChange={(e) => setInviteForm({ ...inviteForm, authType: e.target.value as 'PASSWORD' | 'SAML' })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                                 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                                 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                      >
                        <option value="PASSWORD">{t('admin.users.authTypes.password')}</option>
                        <option value="SAML">{t('admin.users.authTypes.saml')}</option>
                      </select>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {inviteForm.authType === 'SAML' 
                          ? t('admin.users.invite.samlDescription') 
                          : t('admin.users.invite.passwordDescription')}
                      </p>
                    </div>
                  )}
                  {/* Show auth type info for non-BOTH modes */}
                  {authMethod !== 'BOTH' && (
                    <div className="p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {authMethod === 'SAML' 
                          ? t('admin.users.invite.samlDescription') 
                          : t('admin.users.invite.passwordDescription')}
                      </p>
                    </div>
                  )}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowInviteModal(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300
                               rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={inviteUser.isPending}
                      className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg
                               transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {inviteUser.isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {t('admin.users.invite.loading')}
                        </>
                      ) : (
                        <>
                          <Mail className="h-4 w-4" />
                          {t('admin.users.invite.submitButton')}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowEditModal(false)} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="relative bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">{t('admin.users.edit.title')}</h2>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleUpdateUser(editingUser.id, {
                      firstName: editingUser.firstName,
                      lastName: editingUser.lastName,
                      role: editingUser.role,
                      isActive: editingUser.isActive,
                    });
                  }}
                  className="space-y-4"
                >
                  {/* Avatar Upload for Password Users */}
                  {editingUser.authType !== 'SAML' && (
                    <div className="flex items-center gap-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                      <div className="relative">
                        <Avatar
                          src={avatarPreview || editingUser.avatarUrl}
                          firstName={editingUser.firstName}
                          lastName={editingUser.lastName}
                          size="lg"
                        />
                        <label className="absolute bottom-0 right-0 p-1 bg-blue-600 hover:bg-blue-700 text-white rounded-full cursor-pointer transition-colors">
                          <Camera className="h-4 w-4" />
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            onChange={handleAvatarFileSelect}
                            className="hidden"
                            disabled={uploadingAvatar}
                          />
                        </label>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('admin.users.edit.profilePicture')}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {uploadingAvatar ? t('admin.users.edit.uploading') : t('admin.users.edit.uploadInstruction')}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {t('admin.users.edit.uploadLimits')}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* SAML User Info */}
                  {editingUser.authType === 'SAML' && (
                    <div className="p-3 mb-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        {t('admin.users.edit.samlProfileNote')}
                      </p>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('profile.fields.firstName')}
                      </label>
                      <input
                        type="text"
                        value={editingUser.firstName}
                        onChange={(e) => setEditingUser({ ...editingUser, firstName: e.target.value })}
                        required
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                                 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                                 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('profile.fields.lastName')}
                      </label>
                      <input
                        type="text"
                        value={editingUser.lastName}
                        onChange={(e) => setEditingUser({ ...editingUser, lastName: e.target.value })}
                        required
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                                 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                                 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('profile.fields.role')}
                    </label>
                    {agentLoading ? (
                      <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                                    bg-gray-50 dark:bg-gray-800/50 text-gray-900 dark:text-gray-100">
                        Loading...
                      </div>
                    ) : isClientApi ? (
                      <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                                    bg-gray-50 dark:bg-gray-800/50 text-gray-900 dark:text-gray-100">
                        {t('admin.users.roles.admin')}
                        <input type="hidden" value="admin" />
                      </div>
                    ) : (
                      <select
                        value={editingUser.role}
                        onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value as 'admin' | 'user' })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                                 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                                 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                      >
                        <option value="user">{t('admin.users.roles.user')}</option>
                        <option value="admin">{t('admin.users.roles.admin')}</option>
                      </select>
                    )}
                  </div>
                  {authMethod === 'BOTH' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('admin.users.invite.authTypeLabel')}
                      </label>
                      <select
                        value={editingUser.authType}
                        onChange={(e) => setEditingUser({ ...editingUser, authType: e.target.value as 'PASSWORD' | 'SAML' })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                                 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                                 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                      >
                        <option value="PASSWORD">{t('admin.users.authTypes.password')}</option>
                        <option value="SAML">{t('admin.users.authTypes.saml')}</option>
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('admin.users.table.status')}
                    </label>
                    <select
                      value={editingUser.isActive ? 'active' : 'inactive'}
                      onChange={(e) => setEditingUser({ ...editingUser, isActive: e.target.value === 'active' })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                               bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                               focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                    >
                      <option value="active">{t('admin.users.status.active')}</option>
                      <option value="inactive">{t('admin.users.status.inactive')}</option>
                    </select>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300
                               rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg
                               transition-colors"
                    >
                      {t('admin.users.edit.submitButton')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <AlertCircle className="h-5 w-5 text-gray-400 dark:text-gray-500" strokeWidth={1.5} />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {t('admin.users.delete.title')}
                </h2>
              </div>
              
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {t('admin.users.delete.confirmation')}
              </p>
              
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-3">
                  <Avatar
                    src={userToDelete.avatarUrl}
                    firstName={userToDelete.firstName}
                    lastName={userToDelete.lastName}
                    size="md"
                  />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {userToDelete.firstName} {userToDelete.lastName}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {userToDelete.email}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg p-3 mb-6">
                <div className="flex gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <p className="text-sm text-red-700 dark:text-red-300">
                    {t('admin.users.delete.warning')}
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setUserToDelete(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 
                           text-gray-700 dark:text-gray-300 font-medium rounded-lg transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleDeleteUser}
                  disabled={deleteUser.isPending}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 
                           text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed 
                           flex items-center justify-center gap-2"
                >
                  {deleteUser.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('admin.users.delete.deleting')}
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4" />
                      {t('admin.users.delete.confirmButton')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}